import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';

import gtm from './utils/gtm';
import { isDntEnabled } from './utils/dnt';
import { BrowserRouter } from 'react-router';
import { initClientLocale } from './i18n';
import reportWebVitals from './reportWebVitals';
import { collectWebVitalMetrics } from './utils';

import './index.scss';
import { hydrate, QueryClient, QueryClientProvider, } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { TripodLivePreview } from '@moment/layouts/TripodLivePreview/LivePreview.tsx';
import { TabbedSeenUIDsProvider } from '@moment/layouts/TabContainerLayout/TabbedSeenUIDsContext.tsx';
import { TripodStateProvider } from '@moment/context/TripodStateProvider.tsx';
import { CONTENT_NOT_AVAILABLE_PATH } from '@pages/Root/router/constants.ts';

// On page load, check for 'mpid' in the URL query parameters.
// If it exists, save it to localStorage for later use, like in data requests.
try {
  const urlParams = new URLSearchParams(window.location.search);
  const mpid = urlParams.get('mpid');
  if (mpid) {
    localStorage.setItem('mpid', mpid);
  }
} catch (e) {
  console.error('Failed to process mpid from URL:', e);
}

const isContentNotAvailable = window.location.pathname.replace(/\/$/, '') === CONTENT_NOT_AVAILABLE_PATH;

if (!isDntEnabled() && !isContentNotAvailable) {
  gtm.init();
}

const container = document.getElementById('root');

const initialLanguage = window.initialLanguage || navigator.language || 'en';
collectWebVitalMetrics();

if (container) {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 5 * 1000 + 30_000,
        //adjusted due to the randomization
        refetchOnMount: false,
        refetchOnWindowFocus: false,
      },
    },
  });

  const dehydratedState = window.reactQueryData;
  if (dehydratedState) {
    hydrate(queryClient, dehydratedState);
  }

  //element in the router can't be serialized back and forth. for now just inject the live preview.
  const dynamicRoutes = (window.tripodPayload?.dynamicRoutes || []).map(
    (item: any) => {
      return {
        ...item,
        element: (
          <TabbedSeenUIDsProvider>
            <TripodLivePreview pageId={item.id} />
          </TabbedSeenUIDsProvider>
        ),
      };
    }
  );

  initClientLocale(initialLanguage).then((i18nInstance) => {
    ReactDOM.hydrateRoot(
      container,
      <React.StrictMode>
        <I18nextProvider i18n={i18nInstance}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <TripodStateProvider>
                  <App runtimeConfig={null} dynamicRoutes={dynamicRoutes} />
              </TripodStateProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </I18nextProvider>
      </React.StrictMode>
    );
  });

  reportWebVitals();
}
